const Konva = require('konva');
import { loadFontFamily } from './FontsService';
import { findImageBackgroundRect } from '../utils/findImageBackgroundRect';

/** Upper bound for text measure box; keeps Konva textArr complete without max canvas every time. */
const TEXT_MEASURE_CAP = 4000;


function computeTextMeasureInnerRect(
  textObj,
  fullText,
  outerWidth,
  outerHeight,
  substrateToggle,
  substrateLeft,
  substrateRight,
  substrateTop,
  substrateBottom,
) {
  const fontSize = Math.max(1, +textObj.fontSize());
  const lhMult =
    typeof textObj.lineHeight === 'function' ? +textObj.lineHeight() : 1;
  const lineHeightPx = lhMult * fontSize;
  const pad = typeof textObj.padding === 'function' ? +textObj.padding() : 0;

  const innerW0 = substrateToggle
    ? outerWidth - substrateLeft - substrateRight
    : outerWidth;
  const innerH0 = substrateToggle
    ? outerHeight - substrateTop - substrateBottom
    : outerHeight;

  const maxInnerW = substrateToggle
    ? TEXT_MEASURE_CAP - substrateLeft - substrateRight
    : TEXT_MEASURE_CAP;
  const maxInnerH = substrateToggle
    ? TEXT_MEASURE_CAP - substrateTop - substrateBottom
    : TEXT_MEASURE_CAP;

  const safeInnerW = Math.max(1, innerW0);
  const safeInnerH = Math.max(1, innerH0);

  const logicalLines =
    fullText.length > 0 ? fullText.split('\n') : [''];
  const newlineRows = logicalLines.length;
  const flatLen = fullText.replace(/\n/g, '').length;
  const avgCharW = fontSize * 0.55;
  const charsPerLine = Math.max(4, Math.floor(safeInnerW / avgCharW));
  const estWrapLines =
    flatLen > 0 ? Math.max(1, Math.ceil(flatLen / charsPerLine)) : 1;
  const maxLinesAttr = parseInt(textObj.getAttr('maxLines'), 10);
  const capLines =
    !isNaN(maxLinesAttr) && maxLinesAttr > 0 ? maxLinesAttr : 999;
  const lineCountEstimate = Math.min(
    capLines,
    Math.max(newlineRows, estWrapLines) + 2,
  );

  const minMeasureH = lineCountEstimate * lineHeightPx + pad * 2 + 160;

  let maxSegW = 0;
  for (let li = 0; li < logicalLines.length; li++) {
    const seg = logicalLines[li];
    const sample = seg.length > 0 ? seg : ' ';
    const mw = textObj.measureSize(sample).width;
    if (mw > maxSegW) {
      maxSegW = mw;
    }
  }
  if (flatLen > 0 && newlineRows === 1) {
    maxSegW = Math.max(maxSegW, textObj.measureSize(fullText).width);
  }

  const innerW = Math.min(
    maxInnerW,
    Math.max(
      safeInnerW,
      maxSegW + pad * 2 + 160,
      safeInnerW * 2,
      safeInnerW + 400,
    ),
  );
  const innerH = Math.min(
    maxInnerH,
    Math.max(
      safeInnerH,
      minMeasureH,
      safeInnerH * 2,
      safeInnerH + 500,
    ),
  );

  return { innerW, innerH };
}

function formatNumber(value, format) {
  if (format === 'None') {
    return value;
  }
  let number = parseFloat(value);
  if (isNaN(number)) {
    // throw new Error("Invalid number format");
    return value;
  }
  const formats = {
    '1 000 000.00': {
      thousandSeparator: ' ',
      decimalSeparator: '.',
      decimals: true,
    },
    '1 000 000,00': {
      thousandSeparator: ' ',
      decimalSeparator: ',',
      decimals: true,
    },
    '1,000,000.00': {
      thousandSeparator: ',',
      decimalSeparator: '.',
      decimals: true,
    },
    '1.000.000,00': {
      thousandSeparator: '.',
      decimalSeparator: ',',
      decimals: true,
    },
    '1 000 000': {
      thousandSeparator: ' ',
      decimalSeparator: '',
      decimals: false,
    },
    '1,000,000': {
      thousandSeparator: ',',
      decimalSeparator: '',
      decimals: false,
    },
    '1.000.000': {
      thousandSeparator: '.',
      decimalSeparator: '',
      decimals: false,
    },
  };
  const formatOptions = formats[format];
  if (!formatOptions) {
    throw new Error('Invalid format specified');
  }
  const fractionDigits = formatOptions.decimals ? 2 : 0;
  return number
    .toLocaleString('en-US', {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
      useGrouping: true,
    })
    .replace(/,/g, 'TEMP')
    .replace(/\./g, formatOptions.decimalSeparator)
    .replace(/TEMP/g, formatOptions.thousandSeparator);
}

function calculateMath(value, operation, mathValue, mathRound) {
  console.log('calculateMath', value, operation, mathValue, mathRound);
  const num = parseFloat(value);
  const val = parseFloat(mathValue);

  if (isNaN(num) || isNaN(val)) return value;
  if (operation === '/' && val === 0) return value;

  let result;
  switch (operation) {
    case '*':
      result = num * val;
      break;
    case '/':
      result = num / val;
      break;
    case '+':
      result = num + val;
      break;
    case '-':
      result = num - val;
      break;
    default:
      return value;
  }

  console.log('result', result);

  return mathRound ? Math.round(result) : result;
}

/** scale mode fillPattern must match image() bitmap size (changes after autocrop on/off). */
function resolveScalePatternSourceSize(image, initWidth, initHeight) {
  const htmlImage = image.image();
  if (!htmlImage) {
    return { htmlImage: null, srcWidth: initWidth, srcHeight: initHeight };
  }
  const srcWidth = htmlImage.naturalWidth || htmlImage.width || initWidth;
  const srcHeight = htmlImage.naturalHeight || htmlImage.height || initHeight;
  if (srcWidth > 0 && srcHeight > 0) {
    image.setAttr('initWidth', srcWidth);
    image.setAttr('initHeight', srcHeight);
  }
  return { htmlImage, srcWidth, srcHeight };
}

export const scaleImage = (image) => {
  let rect = findImageBackgroundRect(image?.parent);
  if (!rect) return;

  const group = image.getParent();
  const mode = image.getAttr('mode');
  let srcWidth = image.getAttr('initWidth');
  let srcHeight = image.getAttr('initHeight');

  if (mode === 'scale') {
    const resolved = resolveScalePatternSourceSize(image, srcWidth, srcHeight);
    srcWidth = resolved.srcWidth;
    srcHeight = resolved.srcHeight;
  }

  let rectWidth = rect.width() * image.parent.scaleX();
  let rectHeight = rect.height() * image.parent.scaleY();
  const extraDimension = group.getAttr('extraDimension');
  if (extraDimension) {
    rectWidth = extraDimension.width * image.parent.scaleX();
    rectHeight = extraDimension.height * image.parent.scaleY();
    group.setAttr('extraDimension', null);
  }
  const strokeWidth = Math.floor(rect.getAttr('strokeWidth')) || 0;
  const strokePosition = strokeWidth / 2;
  const initialRadius = [0, 0, 0, 0];
  const cornerRadius = rect.getAttr('cornerRadius') || initialRadius;

  rect.size({
    width: rectWidth,
    height: rectHeight,
  });

  let hRatio = rectWidth / srcWidth;
  let vRatio = rectHeight / srcHeight;
  const min_ratio = Math.min(hRatio, vRatio);
  const imagePosition = {
    x: (rectWidth - srcWidth * min_ratio) / 2,
    y: (rectHeight - srcHeight * min_ratio) / 2,
  };

  const imageSize = {
    width: srcWidth * min_ratio - strokeWidth,
    height: srcHeight * min_ratio - strokeWidth,
  };

  if (mode === 'stretch') {
    image.position({
      x: strokeWidth / 2,
      y: strokeWidth / 2,
    });
    const imageWidthStretch = rectWidth - strokeWidth;
    const imageHeightStretch = rectHeight - strokeWidth;

    image.size({
      width: imageWidthStretch > 0 ? imageWidthStretch : 1,
      height: imageHeightStretch > 0 ? imageHeightStretch : 1,
    });
  } else {
    image.position({
      x: imagePosition.x + strokePosition,
      y: imagePosition.y + strokePosition,
    });
    image.size({
      width: imageSize.width > 0 ? imageSize.width : 1,
      height: imageSize.height > 0 ? imageSize.height : 1,
    });

    const dimensionsFloored = {
      imageWidth: Math.floor(image.width()),
      imageHeight: Math.floor(image.height()),
      rectWidth: Math.floor(rectWidth),
      rectHeight: Math.floor(rectHeight),
    };

    if (mode === 'fit') {
      if (cornerRadius) {
        image.setAttr('cornerRadius', initialRadius);
      }

      const averageRadius =
        (cornerRadius[0] +
          cornerRadius[1] +
          cornerRadius[2] +
          cornerRadius[3]) /
        4;
      if (
        dimensionsFloored.imageHeight >
          dimensionsFloored.rectHeight - strokeWidth ||
        dimensionsFloored.imageWidth >
          dimensionsFloored.rectWidth - strokeWidth - averageRadius * 2
      ) {
        image.position({
          x: imagePosition.x + averageRadius / 2,
          y: imagePosition.y + averageRadius / 2,
        });
        image.size({
          width: srcWidth * min_ratio - averageRadius,
          height: srcHeight * min_ratio - averageRadius,
        });
      }
    }
  }

  if (mode !== 'fit') {
    if (cornerRadius) {
      image.setAttr('cornerRadius', cornerRadius);
    }
    if (strokeWidth === 0) {
      rect.fillEnabled(false);
    } else {
      rect.fillEnabled(true);
    }
  } else {
    rect.fillEnabled(true);
  }

  if (mode === 'flex') {
    group.setAttr('extraDimension', {
      width: rect.width(),
      height: rect.height(),
    });
    image.position({
      x: imagePosition.x + strokeWidth / 2,
      y: imagePosition.y + strokeWidth / 2,
    });
    image.size({
      width: imageSize.width,
      height: imageSize.height,
    });

    rect.size({
      width: imageSize.width + strokeWidth,
      height: imageSize.height + strokeWidth,
    });
    rect.position({
      x: imagePosition.x,
      y: imagePosition.y,
    });
  } else {
    rect.position({
      x: 0,
      y: 0,
    });
  }

  const rectBg = rect.getAttr('fill');

  if (rectBg) {
    rect.setAttr('extraBg', rectBg);
  }

  if (mode === 'scale') {
    const { htmlImage, srcWidth: patternW, srcHeight: patternH } =
      resolveScalePatternSourceSize(image, srcWidth, srcHeight);

    image.opacity(0);
    rect.opacity(1);
    rect.fillEnabled(true);
    if (rectBg) {
      rect.setAttr('extraBg', rectBg);
    }
    rect.fill(null);

    if (htmlImage && patternW > 0 && patternH > 0) {
      const patternHRatio = rectWidth / patternW;
      const patternVRatio = rectHeight / patternH;
      const max_ratio = Math.max(patternHRatio, patternVRatio);

      rect.fillPatternImage(htmlImage);
      rect.fillPatternX(0);
      rect.fillPatternY(0);
      rect.fillPatternScale({ x: max_ratio, y: max_ratio });
      rect.fillPatternOffset({
        x: (patternW - rectWidth / max_ratio) / 2,
        y: (patternH - rectHeight / max_ratio) / 2,
      });
    }
  } else {
    const extraBg = rect.getAttr('extraBg');
    if (extraBg) {
      rect.fill(extraBg);
      rect.opacity(1);
    } else {
      rect.opacity(0);
    }
    image.opacity(1);
  }

  image.parent.scale({ x: 1, y: 1 });
};

export const scaleText = (textObj, res, rej) => {
  let fullText = (textObj.getAttr('fullText') ?? '').toString();

  const isEmptyValue = fullText === '';

  if (textObj.getAttr('forceDoubleDigit') && !isNaN(parseFloat(fullText))) {
    fullText = parseFloat(fullText).toFixed(2).toString();
  }

  if (textObj.getAttr('mathOperation') && !isEmptyValue) {
    console.log('math round', textObj.getAttr('mathRound'));
    fullText = calculateMath(
      fullText,
      textObj.getAttr('mathOperation'),
      textObj.getAttr('mathValue'),
      textObj.getAttr('mathRound'),
    ).toString();
  }

  if (textObj.getAttr('numberFormat') && !isEmptyValue) {
    fullText = formatNumber(fullText, textObj.getAttr('numberFormat'));
  }

  if (textObj.getAttr('prefix') && !isEmptyValue) {
    fullText = `${textObj.getAttr('prefix')}${fullText}`;
  }

  if (textObj.getAttr('postfix') && !isEmptyValue) {
    fullText = `${fullText}${textObj.getAttr('postfix')}`;
  }

  if (textObj.getAttr('regexpMatch') && textObj.getAttr('regexpReplace')) {
    const regex = createRegex(textObj.getAttr('regexpMatch'));
    if (regex) {
      fullText = fullText.replace(regex, textObj.getAttr('regexpReplace'));
    }
  }

  switch (textObj.getAttr('textFormat')) {
    case 'upperCase':
      fullText = fullText.toUpperCase();
      break;
    case 'lowerCase':
      fullText = fullText.toLowerCase();
      break;
    case 'withCapital':
      fullText = fullText.charAt(0).toUpperCase() + fullText.slice(1);
      break;
    default:
      break;
  }

  textObj.text(fullText);

  let txtGroup = textObj.parent,
    substrate = txtGroup.findOne('.substrate'),
    bgRect = txtGroup.findOne('.background'),
    vis_text;

  let width = bgRect.width() * txtGroup.scaleX();
  let height = bgRect.height() * txtGroup.scaleY();
  const mode = txtGroup.getAttr('mode') || 'fixed';
  const extraDimForFixed =
    mode === 'fixed' ? txtGroup.getAttr('extraDimension') : null;
  if (
    extraDimForFixed &&
    extraDimForFixed.width > 0 &&
    extraDimForFixed.height > 0 &&
    (!Number.isFinite(width) ||
      !Number.isFinite(height) ||
      width <= 0 ||
      height <= 0)
  ) {
    width = extraDimForFixed.width;
    height = extraDimForFixed.height;
    bgRect.width(width);
    bgRect.height(height);
  }
  const fixedDimension = txtGroup.getAttr('fixedDimension');
  const lastFixedDimension = txtGroup.getAttr('lastFixedDimension');

  if (mode === 'fixed' && (lastFixedDimension || fixedDimension)) {
    const dim = lastFixedDimension || fixedDimension;
    width = dim.width;
    height = dim.height;
    txtGroup.setAttr('lastFixedDimension', null);
    txtGroup.setAttr('fixedDimension', null);
  }

  if (mode === 'fixed') {
    txtGroup.setAttr('extraDimension', null);
  }

  const preFlexWidth = width;
  const preFlexHeight = height;
  if (mode === 'flex') {
    const FLEX_SEED = 4000;
    width = Math.max(width, FLEX_SEED);
    height = Math.max(height, FLEX_SEED);
  }

  if (mode === 'flex' && substrate.getAttr('substrateToggle')) {
    substrate.setAttr('substrateToggle', false);
    substrate.setAttr('substrateTop', 0);
    substrate.setAttr('substrateRight', 0);
    substrate.setAttr('substrateBottom', 0);
    substrate.setAttr('substrateLeft', 0);
    substrate.setAttr('perfectToggle', false);
  }

  const minFontSize = parseInt(textObj.getAttr('minFontSize')),
    maxFontSize = parseInt(textObj.getAttr('maxFontSize')),
    maxLines = parseInt(textObj.getAttr('maxLines')),
    substrateLeft = parseInt(substrate.getAttr('substrateLeft')) || 0,
    substrateRight = parseInt(substrate.getAttr('substrateRight')) || 0,
    substrateTop = parseInt(substrate.getAttr('substrateTop')) || 0,
    substrateBottom = parseInt(substrate.getAttr('substrateBottom')) || 0,
    substrateToggle = substrate.getAttr('substrateToggle'),
    perfectToggle = substrate.getAttr('perfectToggle'),
    align = textObj.align(),
    verticalAlign = textObj.getAttr('verticalAlign');

  function getTextBlockLayoutSize(obj) {
    const arr = obj.textArr || [];
    const fontSize = +obj.fontSize();
    const lhMult =
      typeof obj.lineHeight === 'function' ? +obj.lineHeight() : 1;
    const pad = typeof obj.padding === 'function' ? +obj.padding() : 0;
    let maxLineW = 0;
    for (let i = 0; i < arr.length; i++) {
      const w = arr[i].width;
      if (typeof w === 'number' && w > maxLineW) {
        maxLineW = w;
      }
    }
    const lineHeightPx = lhMult * fontSize;
    const lines = Math.max(arr.length, 1);
    const blockH = lines * lineHeightPx;
    return {
      width: maxLineW + pad * 2,
      height: blockH + pad * 2,
    };
  }

  function mergeTextSizeWithLayoutBounds(obj, pixelSize) {
    const layout = getTextBlockLayoutSize(obj);
    return {
      width: Math.max(pixelSize.width, layout.width),
      height: Math.max(pixelSize.height, layout.height),
      x: pixelSize.x,
      y: pixelSize.y,
    };
  }

  const waitForFont = loadFontFamily(textObj.fontFamily(), 2000).catch((e) => {
    console.warn(
      'Font load failed, fallback to available font:',
      textObj.fontFamily(),
      e,
    );
    return null;
  });

  return Promise.resolve(waitForFont).then(function () {
      // TODO: рендерит не тот шрифт, но после дрега норм...
      const preFlexBgLocalW = bgRect.width();

      bgRect.width(width);
      bgRect.height(height);

      if (width <= 0 || height <= 0) {
        if (mode === 'flex') {
          width = Math.max(1, substrateLeft + substrateRight + 1);
          height = Math.max(1, substrateTop + substrateBottom + 1);
          bgRect.width(width);
          bgRect.height(height);
        } else {
          txtGroup.scale({ x: 1, y: 1 });
          return;
        }
      }
      if (width > 0 && height > 0) {
        if (substrateToggle) {
          if (
            substrateLeft + substrateRight >= width ||
            substrateTop + substrateBottom >= height
          ) {
            txtGroup.scale({ x: 1, y: 1 });
            return;
          }
          textObj.width(width - substrateLeft - substrateRight);
          textObj.height(height - substrateTop - substrateBottom);
        } else {
          textObj.width(width);
          textObj.height(height);
        }
      } else {
        txtGroup.scale({ x: 1, y: 1 });
        return;
      }
      textObj.position({ x: 0, y: 0 });

      let fixedLayoutMeasure = false;
      let fixedMeasureInnerH = 0;
      if (mode === 'fixed' && !textObj.getAttr('dynamicField')) {
        fixedLayoutMeasure = true;
        const mFix = computeTextMeasureInnerRect(
          textObj,
          fullText,
          width,
          height,
          substrateToggle,
          substrateLeft,
          substrateRight,
          substrateTop,
          substrateBottom,
        );
        fixedMeasureInnerH = mFix.innerH;
        textObj.height(mFix.innerH);
        txtGroup.getLayer()?.draw();
      }

      if (textObj.getAttr('dynamicField')) {
        textObj.setAttr('fontSize', maxFontSize);
        txtGroup.getLayer().draw();

        vis_text = collectVisibleText(textObj.textArr);

        if (mode === 'flex') {
          bgRect.fill(bgRect.getAttr('saved_fill'));
        } else {
          while (
            !checkText(vis_text, textObj) &&
            parseInt(textObj.fontSize() / 2) >= minFontSize
          ) {
            textObj.fontSize(parseInt(textObj.fontSize() / 2));
            vis_text = collectVisibleText(textObj.textArr);
          }

          while (
            checkText(vis_text, textObj) &&
            textObj.fontSize() < maxFontSize
          ) {
            textObj.fontSize(textObj.fontSize() + 1);
            vis_text = collectVisibleText(textObj.textArr);
          }

          while (
            !checkText(vis_text, textObj) &&
            textObj.fontSize() > minFontSize
          ) {
            textObj.fontSize(textObj.fontSize() - 1);
            vis_text = collectVisibleText(textObj.textArr);
          }

          const realVisText = getRealVisibleTextForLines(textObj);

          if (!checkText(vis_text, textObj)) {
            if (textObj.getAttr('cutText')) {
              const trimmed =
                realVisText.substring(0, realVisText.length - 3) + '...';
              textObj.text(trimmed);
              bgRect.fill(bgRect.getAttr('saved_fill'));
            } else {
              bgRect.fill('red');
            }
          } else {
            bgRect.fill(bgRect.getAttr('saved_fill'));
          }
        }
      }

      if (mode === 'flex') {
        const flexMeasureText = (textObj.text() ?? fullText).toString();
        const mFlex = computeTextMeasureInnerRect(
          textObj,
          flexMeasureText,
          width,
          height,
          substrateToggle,
          substrateLeft,
          substrateRight,
          substrateTop,
          substrateBottom,
        );
        textObj.width(mFlex.innerW);
        textObj.height(mFlex.innerH);
        txtGroup.getLayer()?.draw();
      }

      let textSize = getTextSize(textObj);
      if (mode === 'flex' || fixedLayoutMeasure) {
        textSize = mergeTextSizeWithLayoutBounds(textObj, textSize);
      }

      if (fixedLayoutMeasure) {
        const innerNeed = textSize.height;
        const innerCurrent = substrateToggle
          ? height - substrateTop - substrateBottom
          : height;
        if (innerNeed > innerCurrent) {
          height = substrateToggle
            ? innerNeed + substrateTop + substrateBottom
            : innerNeed;
          bgRect.height(height);
        }
        if (substrateToggle) {
          textObj.width(width - substrateLeft - substrateRight);
          textObj.height(height - substrateTop - substrateBottom);
        } else {
          textObj.width(width);
          textObj.height(height);
        }
        txtGroup.getLayer()?.draw();
        const innerCurrentAfter = substrateToggle
          ? height - substrateTop - substrateBottom
          : height;
        const skipSecondGetTextSize =
          innerNeed <= innerCurrent &&
          fixedMeasureInnerH <= innerCurrentAfter;
        if (!skipSecondGetTextSize) {
          textSize = getTextSize(textObj);
          textSize = mergeTextSizeWithLayoutBounds(textObj, textSize);
        }
      }
      const visualOffsetY = getVisualOffsetY(textSize, textObj, verticalAlign);

      if (substrateToggle) {
        substrate.position({
          x: perfectToggle && align === 'left' ? 0 : textSize.x,
          y:
            perfectToggle && verticalAlign === 'top'
              ? 0
              : textSize.y + visualOffsetY,
        });

        const substrateWidth =
          textSize.width +
          substrateLeft +
          substrateRight +
          (perfectToggle && align === 'left' ? textSize.x : 0);

        const substrateHeight =
          textSize.height +
          substrateTop +
          substrateBottom +
          (perfectToggle && verticalAlign === 'top' ? textSize.y : 0);

        substrate.size({
          width:
            mode !== 'flex' && perfectToggle && align === 'right'
              ? bgRect.width() - substrate.x()
              : substrateWidth,
          height:
            mode !== 'flex' && perfectToggle && verticalAlign === 'bottom'
              ? bgRect.height() - substrate.y()
              : substrateHeight,
        });

        textObj.position({
          x: substrateLeft,
          y: substrateTop + visualOffsetY,
        });
      } else {
        substrate.size({ width: width, height: height });
        substrate.position({ x: 0, y: 0 });
        textObj.position({ x: 0, y: visualOffsetY });
      }

      if (mode === 'flex') {
        let flexWidth;
        let flexHeight;
        if (substrateToggle) {
          flexWidth = substrate.width();
          flexHeight = substrate.height();
          if (preFlexWidth > flexWidth || preFlexHeight > flexHeight) {
            txtGroup.setAttr('fixedDimension', {
              width: preFlexWidth,
              height: preFlexHeight,
            });
          }
          bgRect.position(substrate.position());
          bgRect.size({ width: flexWidth, height: flexHeight });
        } else {
          flexWidth =
            textSize.width + substrateLeft + substrateRight;
          flexHeight =
            textSize.height + substrateTop + substrateBottom;
          if (preFlexWidth > flexWidth || preFlexHeight > flexHeight) {
            txtGroup.setAttr('fixedDimension', {
              width: preFlexWidth,
              height: preFlexHeight,
            });
          }
          bgRect.size({ width: flexWidth, height: flexHeight });
          bgRect.position({ x: 0, y: 0 });
          substrate.size({ width: flexWidth, height: flexHeight });
          substrate.position({ x: 0, y: 0 });
          textObj.position({
            x: substrateLeft,
            y: substrateTop + visualOffsetY,
          });
        }
        const textContentWidth = Math.max(1, flexWidth - substrateLeft - substrateRight);
        const textContentHeight = Math.max(1, flexHeight - substrateTop - substrateBottom);
        const actualTextWidth = Math.max(textContentWidth, textSize.width);
        const actualTextHeight = Math.max(textContentHeight, textSize.height);
        if (actualTextWidth > textContentWidth || actualTextHeight > textContentHeight) {
          flexWidth = actualTextWidth + substrateLeft + substrateRight;
          flexHeight = actualTextHeight + substrateTop + substrateBottom;
          bgRect.size({ width: flexWidth, height: flexHeight });
          substrate.size({ width: flexWidth, height: flexHeight });
        }
        textObj.width(actualTextWidth);
        textObj.height(actualTextHeight);
        txtGroup.getLayer()?.draw();
        let flexFinalTextSize = getTextSize(textObj);
        flexFinalTextSize = mergeTextSizeWithLayoutBounds(
          textObj,
          flexFinalTextSize,
        );
        const flexFinalVisualY = getVisualOffsetY(
          flexFinalTextSize,
          textObj,
          verticalAlign,
        );
        textObj.position({
          x: substrateLeft,
          y: substrateTop + flexFinalVisualY,
        });
        txtGroup.setAttr('extraDimension', {
          width: flexWidth,
          height: flexHeight,
        });
        // Горизонтальный «якорь» flex: локальная дельта ширины × scale → сдвиг в координатах родителя.
        const flexAlignH = textObj.align() || 'left';
        const flexScaleX = txtGroup.scaleX();
        const dLocalW = preFlexBgLocalW - flexWidth;
        if (flexAlignH === 'right') {
          txtGroup.x(txtGroup.x() + dLocalW * flexScaleX);
        } else if (flexAlignH === 'center') {
          txtGroup.x(txtGroup.x() + (dLocalW * flexScaleX) / 2);
        }
      } else {
        bgRect.position({ x: 0, y: 0 });
        txtGroup.setAttr('extraDimension', null);
      }

      txtGroup.scale({ x: 1, y: 1 });
      const layer = txtGroup.getLayer();
      if (layer) layer.draw();
      if (res) res();
    }).catch(function (e) {
      console.error('Error scaling text', textObj.fontFamily(), e);
      if (rej) {
        rej(e);
      }
    });

  function getRealVisibleTextForLines(textObject) {
    let visibleText = '';
    let lineCount = 0;

    for (let textLine of textObject.textArr) {
      let textString =
        visibleText.length > 0 ? ' ' + textLine.text : textLine.text;

      if (lineCount === maxLines) {
        let i = 1;
        while (textObject.measureSize(visibleText).width < width - 10) {
          visibleText = visibleText + textString.slice(0, i);
          i++;
        }
        textString = '';
      }

      visibleText = visibleText + textString;

      if (lineCount === maxLines) {
        break;
      }
      ++lineCount;
    }

    return visibleText;
  }

  function getVisualOffsetY(textSize, textObject, verticalAlignValue) {
    if (!textSize || !textObject || !textObject.height()) {
      return 0;
    }

    const textHeight = textObject.height();
    const currentTop = textSize.y;

    switch (verticalAlignValue) {
      case 'top':
        return -currentTop;
      case 'bottom':
        return textHeight - textSize.height - currentTop;
      default: // middle
        return (textHeight - textSize.height) / 2 - currentTop;
    }
  }

  function getTextSize(obj) {
    const initObjectGroup = obj.findAncestor('Group');


    const __stageW = Math.max(1, Math.ceil(obj.width() || 1));
    const __stageH = Math.max(1, Math.ceil(obj.height() || 1));

    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.left = '-99999px';
    container.style.top = '0';
    container.style.width = '0';
    container.style.height = '0';
    container.style.overflow = 'hidden';
    container.width = __stageW;
    container.height = __stageH;

    const copy_canvas = document.createElement('canvas');
    const copy = copy_canvas.getContext('2d');

    document.body.insertBefore(container, document.getElementById('root'));

    const stage = new Konva.Stage({
      container,
      width: __stageW,
      height: __stageH,
    });
    const layer = new Konva.Layer();

    layer.add(obj);

    stage.add(layer);

    let result;
    try {
      stage.draw();

    let layerCanvas = layer.getCanvas();
    let pixelRatio = layerCanvas.getPixelRatio();
    let canvasEl = layerCanvas._canvas;

    let ctx = canvasEl.getContext('2d', { willReadFrequently: true }),
      // Use the Canvas Image Data API, in order to get all the
      // underlying pixels data of that canvas. This will basically
      // return an array (Uint8ClampedArray) containing the data in the
      // RGBA order. Every 4 items represent one pixel.
      pixels = ctx.getImageData(0, 0, canvasEl.width, canvasEl.height),
      // total pixels
      l = pixels.data.length,
      // main loop counter and pixels coordinates
      i,
      x,
      y,
      // an object that will store the area that isn't transparent
      bound = { top: null, left: null, right: null, bottom: null };

    // Minimum alpha to count as a visible pixel.
    // Filters out faint anti-aliasing artifacts that extend beyond the
    // visual glyph boundary (especially at the bottom for chars without
    // descenders), which would otherwise inflate the measured height.
    var ALPHA_THRESHOLD = 15;

    // for every pixel in there
    for (i = 0; i < l; i += 4) {
      // if the alpha value is above threshold (skip faint AA fringe)
      if (pixels.data[i + 3] > ALPHA_THRESHOLD) {
        // find it's coordinates
        x = (i / 4) % canvasEl.width;
        y = ~~(i / 4 / canvasEl.width);

        // store/update those coordinates
        // inside our bounding box Object

        if (bound.top === null) {
          bound.top = y;
        }

        if (bound.left === null) {
          bound.left = x;
        } else if (x < bound.left) {
          bound.left = x;
        }

        if (bound.right === null) {
          bound.right = x;
        } else if (bound.right < x) {
          bound.right = x;
        }

        if (bound.bottom === null) {
          bound.bottom = y;
        } else if (bound.bottom < y) {
          bound.bottom = y;
        }
      }
    }

    // bound.* are indices of the first/last pixel above ALPHA_THRESHOLD.
    // Glyphs always have an AA fringe ~1 device-pixel beyond that core,
    // and substrate sized to the bare core leaves visible 1px gaps on
    // every side (most noticeable on big bold glyphs / digits). Expand
    // by 1 device-pixel on each side, clamped to canvas bounds.
    const AA_BLEED = 1;
    const boundLeft = Math.max(0, bound.left - AA_BLEED);
    const boundTop = Math.max(0, bound.top - AA_BLEED);
    const boundRight = Math.min(canvasEl.width - 1, bound.right + AA_BLEED);
    const boundBottom = Math.min(canvasEl.height - 1, bound.bottom + AA_BLEED);

    // bound.* are inclusive on both ends → +1 to convert to span size.
    var trimHeight = boundBottom - boundTop + 1,
      trimWidth = boundRight - boundLeft + 1;

    if (trimWidth && trimHeight) {
      var trimmed = ctx.getImageData(
        boundLeft,
        boundTop,
        trimWidth,
        trimHeight,
      );

      // Draw back the ImageData into the canvas
      copy.canvas.width = trimWidth;
      copy.canvas.height = trimHeight;
      copy.putImageData(trimmed, 0, 0);

      const textHeight = copy.canvas.height;
      const textWidth = copy.canvas.width;
      // Convert from device-pixel coords to logical (Konva) coords
      result = {
        width: textWidth / pixelRatio,
        height: textHeight / pixelRatio,
        x: boundLeft / pixelRatio,
        y: boundTop / pixelRatio,
      };
    } else {
      result = {
        width: (trimWidth || 0) / pixelRatio,
        height: (trimHeight || 0) / pixelRatio,
        x: (bound.left || 0) / pixelRatio,
        y: (bound.top || 0) / pixelRatio,
      };
    }
    } finally {
      if (initObjectGroup) initObjectGroup.add(obj);
      try { stage.destroy(); } catch (e) { /* ignore */ }
      copy_canvas.remove();
      container.remove();
    }
    return result;
  }

  function checkText(visible_text, textObj) {
    let brokenWords = hasBrokenWords(textObj.getAttr('text'), textObj.textArr);
    let txtSize = getTextSize(textObj);
    return (
      !brokenWords &&
      textObj.textArr.length <= maxLines &&
      txtSize.height <= textObj.height() &&
      txtSize.width <= textObj.width()
    );
  }

  function hasBrokenWords(sourceTokens, renderLines) {
    let combined = collectVisibleText(renderLines);

    let a = sourceTokens;
    let b = combined;

    if (a.length !== b.length) {
      return true;
    }

    for (let i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) {
        return true;
      }
    }

    return false;
  }

  function collectVisibleText(textArr) {
    let combined_text = '';
    for (let i = 0; i < textArr.length; i++) {
      combined_text +=
        (i === 0 ||
        textArr[i - 1].text.endsWith('-') ||
        textArr[i].text.startsWith('-')
          ? ''
          : ' ') + textArr[i].text;
    }
    return combined_text;
  }
};

export const scaleShape = (shape) => {
  let scaleX = shape.parent.scaleX();
  let scaleY = shape.parent.scaleY();

  const width = shape.width() * scaleX;
  const height = shape.height() * scaleY;

  if (!isNaN(width) && width > 0) {
    shape.width(width);
  }

  if (!isNaN(height) && height > 0) {
    shape.height(height);
  }

  shape.parent.scale({ x: 1, y: 1 });
};

export const scaleEllipse = (shape) => {
  let scaleX = shape.parent.scaleX();
  let scaleY = shape.parent.scaleY();

  const width = shape.width() * scaleX;
  const height = shape.height() * scaleY;

  if (!isNaN(width) && width > 0) {
    shape.width(width);
  }

  if (!isNaN(height) && height > 0) {
    shape.height(height);
  }

  shape.setAttr('offset', { x: -width / 2, y: -height / 2 });

  shape.parent.scale({ x: 1, y: 1 });
};

const createRegex = (value) => {
  try {
    return new RegExp(value);
  } catch (e) {
    return undefined;
  }
};

export const scaleRect = (rectGroup) => {
  const scale = rectGroup.scale();
  const rect = rectGroup.findOne('.background');

  if (!rect) return;

  rect.size({
    width: rect.width() * scale.x,
    height: rect.height() * scale.y,
  });

  rectGroup.scale({
    x: 1,
    y: 1,
  });
};

const applyChildScale = (target, scale) => {
  switch (target.getAttr('elementType')) {
    case 'text':
      scaleText(target.findOne('.main'));
      break;
    case 'image':
      scaleImage(target.findOne('.main'));
      break;
    case 'group':
      scaleGroup(target, scale);
      break;
    case 'rect':
      scaleRect(target);
      break;
    default:
      break;
  }
};

export const scaleGroup = (group, scale) => {
  if (group.getAttr('elementType') === 'group') {
    const newScale = scale ?? group.scale();
    for (const child of group.children) {
      child.position({
        x: child.x() * newScale.x,
        y: child.y() * newScale.y,
      });
      if (['text', 'image'].includes(child.getAttr('elementType'))) {
        child.scale(newScale);
      } else if (child.getAttr('elementType') !== 'group') {
        child.scale({
          x: child.scaleX() * newScale.x,
          y: child.scaleY() * newScale.y,
        });
      }
      applyChildScale(child, newScale);
    }
    group.scale({ x: 1, y: 1 });
  }
};
